import { useSyncExternalStore } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { ContextMenuItem } from "lib/@components/context-menu-bar/context-menu-item"
import { CloseSnackBar } from "event-definitions"
import { snackbar } from "lib/snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { useParams } from "react-router"
import { downloadAuthoringPDF } from "./controller/download-authoring-pdf"
import { confirm } from "lib/@dialogs/confirm"
import { Box, Tooltip, Typography } from "@mui/material"
import { AMENDS_AUDIENCE_EXTERNAL, AMENDS_AUDIENCE_INTERNAL } from "../project/amends/lib/audience-aware-amends.mjs"

const DOWNLOAD_SNACKBAR_KEY = "authoring-pdf-download"
const downloadSubscribers = new Set()
let isDownloadingPdf = false

export function DownloadAuthoringPdfMenuItem({ mode, audience = AMENDS_AUDIENCE_INTERNAL, label, dataCy }) {
    const { projectId } = useParams()
    const { targetId, schedule, status } = useBoundContext()
    const downloading = useSyncExternalStore(subscribeToDownloadState, getDownloadStateSnapshot)
    const disabled = status === null
    const disabledMessage = disabled ? "PDF download is not available without a draft" : ""

    if (!projectId || !targetId) return null

    return (
        <Tooltip
            title={disabled ? disabledMessage : ""}
            slotProps={{
                popper: {
                    sx: {
                        zIndex: 2002,
                    },
                },
            }}
        >
            <Box>
                <ContextMenuItem
                    icon={getMenuIcon()}
                    label={label}
                    noTitleize
                    data-cy={dataCy}
                    onClick={handleDownload}
                    disabled={disabled || downloading}
                />
            </Box>
        </Tooltip>
    )

    async function handleDownload() {
        if (downloading) return
        const confirmed = await confirmInternalDownload()
        if (!confirmed) return

        try {
            setDownloadState(true)
            snackbar("Downloading...", {
                variant: "info",
                persist: true,
                key: DOWNLOAD_SNACKBAR_KEY,
            })
            const pdfString = await downloadAuthoringPDF({ projectId, scheduleId: targetId, mode, audience })
            if (!pdfString) {
                errorSnackbar("PDF data not received")
                return
            }

            downloadPDFObject(pdfString, buildFileName({ schedule, mode, audience }))
        } catch (error) {
            errorSnackbar("Error downloading PDF")
        } finally {
            CloseSnackBar.raiseLater(DOWNLOAD_SNACKBAR_KEY)
            setDownloadState(false)
        }
    }

    function getMenuIcon() {
        if (downloading) return "ic:round-hourglass-empty"
        return audience === AMENDS_AUDIENCE_EXTERNAL ? "mdi:account-outline" : "mdi:shield-lock-outline"
    }

    async function confirmInternalDownload() {
        if (audience !== AMENDS_AUDIENCE_INTERNAL) return true

        return confirm(
            <Typography variant="body2">
                This internal PDF will include internal notes. Only continue if this copy is for internal use.
            </Typography>,
            "Download internal PDF"
        )
    }
}

function subscribeToDownloadState(onStoreChange) {
    downloadSubscribers.add(onStoreChange)

    return function unsubscribeFromDownloadState() {
        downloadSubscribers.delete(onStoreChange)
    }
}

function getDownloadStateSnapshot() {
    return isDownloadingPdf
}

function setDownloadState(nextValue) {
    isDownloadingPdf = nextValue
    for (const subscriber of downloadSubscribers) {
        subscriber()
    }
}

function buildFileName({ schedule = {}, mode, audience = AMENDS_AUDIENCE_INTERNAL } = {}) {
    const code = slugify(schedule?.code) || "unknown-code"
    const audienceLabel = audience === AMENDS_AUDIENCE_EXTERNAL ? "customer" : "internal"
    return `schedule-review-${audienceLabel}-${slugify(mode)}-${code}.pdf`
}

function slugify(value = "") {
    return String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
}

function downloadPDFObject(data, filename = "file.pdf") {
    const base64 = String(data).replace(/^data:application\/pdf;base64,/, "")
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
    const blob = new Blob([bytes], { type: "application/pdf" })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
}
